/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */
import {Container} from 'pixi.js';
import {ComputedSignal, Store} from '@gongxigames/framework/core';
import {
    FC,
    $Container,
    CanBeSignals, getProp,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Size2D} from '@gongxigames/framework/core/types/Size2D';
import {$OnResourcesLoaded} from '@gongxigames/framework/slotV2/JSX/OnResourcesLoaded';
import {$GameAlignContainer} from '@gongxigames/framework/slotV2/JSX/GameAlignContainer';
import {LSViewConfig} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/loadingScreenViewConfig';
import {getOrientation} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/utils/getOrientation';
import {Renderers, SlidesComponents} from '@gongxigames/framework/slotV2/modules/loadingScreenV2/types';

export class LoadingScreen {
    public container!: Container;
    public onStartButtonClicked!: () => void;
    private isLoadingComplete = Store.signal(false);
    private progress = Store.signal(0);

    private isStartButtonVisible: ComputedSignal<boolean> = Store.computed(() => {
        return this.isLoadingComplete.value &&
            !LSViewConfig.startButtonParams.shouldShowFullScreenButton;
    });

    constructor(
        private renderers: Renderers,
        private slidesComponents: SlidesComponents,
    ) {
        this.createScene();
    }

    public updateProgress(progress: number): void {
        this.progress.value = progress;
    }

    public completeLoading(): void {
        Store.batch(() => {
            this.isLoadingComplete.value = true;
            this.progress.value = 1;
        });
    }

    private createScene(): void {
        this.container = $LoadingScreen({
            backgroundSize: {width: 1920, height: 1920},
            complete: this.isLoadingComplete,
            progress: this.progress,
            onStartButtonClicked: () => this.onStartButtonClicked?.(),
            renderers: this.renderers,
            slidesComponents: this.slidesComponents,
            shouldShowFullScreenButton: LSViewConfig.startButtonParams.shouldShowFullScreenButton,
            isStartButtonVisible: this.isStartButtonVisible,
        }) as Container;
    }
}

type LoadingScreenProps = CanBeSignals<{
    backgroundSize: Size2D;
    complete: boolean;
    progress: number;
    onStartButtonClicked: () => void;
}> & {
    isStartButtonVisible: ComputedSignal<boolean>;
    shouldShowFullScreenButton: boolean;
    renderers: Renderers;
    slidesComponents: SlidesComponents;
};

const $LoadingScreen: FC<LoadingScreenProps> = (props) => {
    const onStartButtonClicked = getProp(props.onStartButtonClicked);
    const r = props.renderers;
    return (
        <$Container>
            <$OnResourcesLoaded resources='beforePriority'>
                {() => (
                    <>
                        {r.renderBackground()}
                        {r.renderLogo()}
                        <$GameAlignContainer align={[0.5, 0.5]}>
                            <$Container
                                position={getOrientation(LSViewConfig.generalContainerParams.position)}
                                scale={getOrientation(LSViewConfig.generalContainerParams.scale)}
                            >
                                {r.renderSlides(props.slidesComponents)}
                                {r.renderProgress(props.progress)}
                                {r.renderStartButton(onStartButtonClicked, props.isStartButtonVisible)}
                                {r.renderBanner(props.complete)}
                            </$Container>
                        </$GameAlignContainer>

                        {r.renderFullScreenButton(onStartButtonClicked, props.isStartButtonVisible, props.shouldShowFullScreenButton)}
                    </>
                )}
            </$OnResourcesLoaded>
        </$Container>
    );
};